import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { PageTransition, JellyButton } from "../components/common";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("verifying"); // verifying, success, error, no-token
  const [errorMessage, setErrorMessage] = useState("");
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();

  const verifyEmail = useCallback(async () => {
    try {
      setStatus("verifying");
      const res = await axios.post(`${API}/auth/verify-email?token=${token}`);
      
      // Store the new token
      localStorage.setItem("token", res.data.token);
      
      // Refresh auth state
      await checkAuth();
      
      setStatus("success");
      toast.success("Email verified successfully!");
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } catch (err) {
      setStatus("error");
      setErrorMessage(err.response?.data?.detail || "Verification failed. Please try again.");
      toast.error(err.response?.data?.detail || "Verification failed");
    }
  }, [token, checkAuth, navigate]);

  useEffect(() => {
    if (!token) {
      setStatus("no-token");
      return;
    }
    
    verifyEmail();
  }, [token, verifyEmail]);

  const handleResendVerification = async () => {
    try {
      const authToken = localStorage.getItem("token");
      if (!authToken) {
        toast.error("Please log in to resend verification email");
        navigate("/login");
        return;
      }
      
      await axios.post(`${API}/auth/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      toast.success("Verification email sent! Check your inbox.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend verification email");
    }
  };

  const renderContent = () => {
    switch (status) {
      case "verifying":
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-violet-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
              Verifying Your Email
            </h1>
            <p className="text-slate-600">
              Please wait while we verify your email address...
            </p>
          </div>
        );
      
      case "success":
        return (
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
              className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </motion.div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
              Email Verified! 🎉
            </h1>
            <p className="text-slate-600 mb-6">
              Your email has been verified successfully. Redirecting to dashboard...
            </p>
            <JellyButton variant="primary" onClick={() => navigate("/dashboard")}>
              Go to Dashboard
            </JellyButton>
          </div>
        );
      
      case "error":
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
              Verification Failed
            </h1>
            <p className="text-slate-600 mb-6">
              {errorMessage}
            </p>
            <div className="space-y-3">
              <JellyButton variant="primary" onClick={handleResendVerification} className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" /> Resend Verification Email
              </JellyButton>
              <button
                onClick={() => navigate("/login")}
                className="w-full h-12 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-colors"
              >
                Back to Login
              </button>
            </div>
          </div>
        );
      
      case "no-token":
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <Mail className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
              Check Your Email
            </h1>
            <p className="text-slate-600 mb-6">
              {user && !user.email_verified ? (
                <>We sent a verification link to <strong>{user.email}</strong>. Click the link to verify your account.</>
              ) : (
                "Please check your email for a verification link, or request a new one below."
              )}
            </p>
            <div className="space-y-3">
              {user && !user.email_verified && (
                <JellyButton variant="primary" onClick={handleResendVerification} className="w-full">
                  <RefreshCw className="w-4 h-4 mr-2" /> Resend Verification Email
                </JellyButton>
              )}
              <button
                onClick={() => navigate(user ? "/dashboard" : "/login")}
                className="w-full h-12 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-colors"
              >
                {user ? "Go to Dashboard" : "Back to Login"}
              </button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen hero-gradient pt-24 pb-16 px-4 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <div className="bg-white rounded-3xl shadow-xl p-8">
            {renderContent()}
          </div>
        </motion.div>
      </div>
    </PageTransition>
  );
};

export default VerifyEmailPage;
